import { useState, useEffect } from 'react';
import type { MultiSelectOptions } from '@hotjar/hotsauce';
import {
  BasePopover,
  Button,
  Field,
  Link,
  MultiSelect,
  Select,
  Stack,
  Text,
  TextInput,
  tokens,
  useNotify,
} from '@hotjar/hotsauce';
import type { FieldError } from 'react-hook-form';
import { useForm, Controller } from 'react-hook-form';
import type { Organization } from '@hotjar/organization';
import { useOrganizations } from '@hotjar/organization';
import styled from '@emotion/styled';
import { usePageTitle } from '@hotjar/webapp/hooks/usePageTitle';
import { Trans, useTranslation } from '@hotjar/translations';
import { useSites } from '@hotjar/site';
import { useNavigate } from 'react-router-dom';
import { SITE } from '@hotjar/webapp/constants/routes';
import { useAuthorizationWithUserId } from '../../domains/authorization';
import { DetailCard } from './DetailCard';
import type { PublicApiKey } from '../../domains/public-api';
import { createPublicApiKey, scopesOptions, usePublicApiKey } from '../../domains/public-api';
import type { PublicApiKeyCreateResponse } from '../../domains/public-api/types';
import { NewApiKeyModal } from './NewApiKeyModal';
import { DeleteApiKeyModal } from './DeleteApiKeyModal';

interface PublicApiForm {
  orgId: number;
  name: string;
  scopes: MultiSelectOptions;
}

const Wrapper = styled.div`
  width: 800px;
  margin: 0 auto;
  padding-top: ${tokens.spacer.x6};
`;

const PageTitle = styled(Text)`
  margin-bottom: ${tokens.spacer.x2};
`;

const Description = styled(Text)`
  margin-bottom: ${tokens.spacer.x3pt5};
`;

const SectionTitle = styled(Text)`
  margin-bottom: ${tokens.spacer.x2pt5};
`;

const StyledForm = styled.form`
  margin-bottom: ${tokens.spacer.x6};
`;

export const PublicApi = ({ userId }: { userId?: number }): JSX.Element => {
  const { t } = useTranslation('auth');
  const { register, errors, handleSubmit, control, getValues, reset } = useForm<PublicApiForm>();
  const [isNewKeyModalOpen, setIsNewKeyModalOpen] = useState(false);
  const [orgsOnScale, setOrgsOnScale] = useState<number[]>([]);
  const [isCurrentOrgOnScale, setIsCurrentOrgOnScale] = useState<boolean>(false);
  const [isDeleteKeyModalOpen, setIsDeleteKeyModalOpen] = useState(false);
  const [ownedOrganizations, setOwnedOrganizations] = useState<Organization[]>([]);
  const [newKey, setNewKey] = useState<PublicApiKeyCreateResponse | null>(null);
  const [deleteKey, setDeleteKey] = useState<PublicApiKey | null>(null);
  const { hasPermission } = useAuthorizationWithUserId(userId);
  const { data: organizations } = useOrganizations(userId);
  const {
    query: { data: sites },
  } = useSites();
  const { data: apiKeys, refetch } = usePublicApiKey();
  const { showToast } = useNotify();
  const navigate = useNavigate();

  usePageTitle(`${t('public-api.title')} - Hotjar`);

  useEffect(() => {
    if (!organizations) return;
    const getOwnedOrganizations = organizations?.filter((item) => {
      return hasPermission('organization', `${item.id}`, 'api-key-create');
    });

    setOwnedOrganizations(getOwnedOrganizations);
  }, [organizations, hasPermission]);

  useEffect(() => {
    if (!sites) return;
    const getOrgsOnScale = sites
      .filter(
        ({ effectivePlan, effectiveAskPlan }) =>
          effectivePlan === 'scale' || effectiveAskPlan === 'scale',
      )
      .map((site) => site.organizationId);

    setOrgsOnScale(getOrgsOnScale);
  }, [sites]);

  const handleOrgChange = () => {
    setIsCurrentOrgOnScale(orgsOnScale.includes(Number(getValues('orgId'))));
  };

  const onCreate = async (data: PublicApiForm) => {
    try {
      const scopesSelectedValues = data.scopes.map((item) => item.value);
      const response = await createPublicApiKey(data.orgId, {
        display_name: data.name,
        scopes: scopesSelectedValues,
      });
      setNewKey(response);
      setIsNewKeyModalOpen(true);
    } catch (e) {
      showToast({
        text: t('public-api.new-key.error-toast'),
        kind: 'danger',
      });
    }
  };

  const onDelete = async (data: PublicApiKey) => {
    setDeleteKey(data);
    setIsDeleteKeyModalOpen(true);
  };

  const handleDeleteKeyModalClose = async () => {
    setIsDeleteKeyModalOpen(false);
    setDeleteKey(null);
    await refetch();
  };

  const handleNewKeyModalClose = async () => {
    setIsNewKeyModalOpen(false);
    setNewKey(null);
    reset({
      orgId: undefined,
      name: '',
      scopes: [],
    });
    await refetch();
  };

  const scopesError: FieldError | undefined = errors.scopes as unknown as FieldError | undefined;

  return (
    <Wrapper>
      <PageTitle as="h1" textType="header3">
        {t('public-api.title')}
      </PageTitle>
      <Description as="p">
        <Trans i18nKey="auth:public-api.description">
          <Link
            kind="inline"
            as="a"
            isExternal
            href="https://help.hotjar.com/hc/en-us/articles/12244109929751"
          >
            Read the full documentation
          </Link>
        </Trans>
      </Description>
      <SectionTitle as="h2" textType="header5">
        {t('public-api.generate-key-title')}
      </SectionTitle>

      <StyledForm onSubmit={handleSubmit(onCreate)} data-testid="public-api-form">
        <Stack preset="form">
          <Field
            labelText={t('public-api.organization')}
            inputId="orgId"
            isInvalid={!!errors.orgId}
            invalidText={errors?.orgId?.message}
          >
            <Select
              id="orgId"
              name="orgId"
              ref={register({ required: t<string>('public-api.organization-required') })}
              isInvalid={!!errors.orgId}
              onChange={handleOrgChange}
            >
              <option value="">{t('public-api.select-organization')}</option>
              {ownedOrganizations?.map((item) => (
                <option value={item.id} key={`Org_${item.id}`}>
                  {item.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            labelText={t('public-api.key-name')}
            inputId="name"
            isInvalid={!!errors.name}
            invalidText={errors?.name?.message}
          >
            <TextInput
              placeholder={t('public-api.key-placeholder')}
              id="name"
              name="name"
              ref={register({ required: t<string>('public-api.key-name-required') })}
              isInvalid={!!errors.name}
            />
          </Field>

          <Field
            labelText={t('public-api.scope')}
            inputId="scopes"
            isInvalid={!!scopesError}
            invalidText={scopesError?.message}
          >
            <Controller
              control={control}
              name="scopes"
              defaultValue=""
              rules={{ required: t<string>('public-api.scope-required') }}
              render={({ value, onChange }) => (
                <MultiSelect
                  availableOptions={scopesOptions}
                  selectedOptions={value}
                  placeholder={t('public-api.select-scope')}
                  onChange={onChange}
                  size="s"
                  isInvalid={!!scopesError}
                />
              )}
            />
          </Field>
          <Stack>
            {isCurrentOrgOnScale ? (
              <Button type="submit">{t('public-api.submit-button')}</Button>
            ) : (
              <BasePopover
                trigger="mouseenter focus"
                title={t('public-api.upgrade.title')}
                content={t('public-api.upgrade.content')}
                actionText={t('public-api.upgrade.button')}
                withCloseButton={false}
                placement="top-start"
                onActionPress={(e) => {
                  e.stopPropagation();
                  navigate(SITE.LIST.path);
                }}
              >
                <span>
                  <Button isDisabled>{t('public-api.submit-button')}</Button>
                </span>
              </BasePopover>
            )}
          </Stack>
        </Stack>
      </StyledForm>

      <NewApiKeyModal newKey={newKey} isOpen={isNewKeyModalOpen} onClose={handleNewKeyModalClose} />

      <DeleteApiKeyModal
        deleteKey={deleteKey}
        isOpen={isDeleteKeyModalOpen}
        onClose={handleDeleteKeyModalClose}
      />

      {apiKeys?.length ? (
        <>
          <SectionTitle as="h2" textType="header5">
            {t('public-api.generated-keys')}
          </SectionTitle>

          <Stack direction="column" spacing="x2">
            {apiKeys.map((item) => (
              <DetailCard key={`DetailCard_${item.keyId}`} data={item} onDelete={onDelete} />
            ))}
          </Stack>
        </>
      ) : null}
    </Wrapper>
  );
};
